import MfaProgressCard from './MfaProgressCard';

// Items without a `group` render together under no header (matches
// departments like Infosec/H&S that only have one or two ungrouped items).
function groupItems(items) {
  const groups = new Map();
  for (const item of items) {
    const key = item.group || null;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  }
  return groups;
}

function ItemCard({ item }) {
  const ItemIcon = item.icon;
  return (
    <a
      href={item.url}
      className="flex flex-col rounded-lg border border-gray-200 bg-white p-5 shadow-sm transition hover:shadow-md"
    >
      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-brand-light">
        <ItemIcon className="h-5 w-5 text-brand" />
      </div>
      <h2 className="text-base font-semibold text-gray-900">{item.name}</h2>
      <p className="mt-1 flex-1 text-sm text-gray-500">{item.description}</p>
      <span className="mt-3 self-end text-sm font-medium text-brand">Access &rarr;</span>
    </a>
  );
}

// Department access (checked before this component ever renders — see
// filterDepartmentsByAccess) only gets you as far as the department view.
// Within it, an item with a headerKey is only shown if the user is also a
// member of that header's group — mirrors the RequireGroup gate the
// destination app itself enforces, so the nav doesn't show a link that
// would just bounce the user straight back.
function visibleItems(items, access) {
  return items.filter((item) => !item.headerKey || access?.headers?.includes(item.headerKey));
}

function ModulePanel({ departmentKey, departments, access }) {
  const dept = departments.find((d) => d.key === departmentKey);
  if (!dept) return null;

  const DeptIcon = dept.icon;
  const items = visibleItems(dept.items, access);
  const groups = groupItems(items);

  return (
    <div className="p-8">
      <div className="mb-8 flex items-center gap-3">
        <DeptIcon className="h-9 w-9 text-brand" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{dept.fullName || dept.name}</h1>
          <p className="text-sm text-gray-500">{dept.description}</p>
        </div>
      </div>

      {/* IT-only: live MFA Authenticator-only campaign progress. The tech app
          owns the data; the card fetches it through the same-origin proxy. */}
      {departmentKey === 'it' && (
        <div className="mb-8">
          <MfaProgressCard />
        </div>
      )}

      <div className="space-y-8">
        {Array.from(groups.entries()).map(([groupName, items]) => (
          <div key={groupName ?? '__ungrouped'}>
            {groupName && (
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">{groupName}</h3>
            )}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((item) => (
                <ItemCard key={item.name} item={item} />
              ))}
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-gray-400">Nothing here yet.</p>}
      </div>
    </div>
  );
}

export default ModulePanel;
